<template>
  <div class="transfer-status">
    <h3>{{ tdc.tdc_allocationStatus }}</h3>
    <div class="products card" v-for="product in products" :key="product.id">
      <header>
        <div class="product-img">
          <img
            v-if="product.thumbnail"
            :src="product.thumbnail"
            :alt="product.imageAltText"
          />
        </div>
        <h5 v-if="product.title">{{product.title}}</h5>
        <div class="remaining">
          <div class="num-available">
            <div class="before-block"></div>
            <div class="middle-block" :id="'sharableQuantity_' + product.id">
              {{ (qualifications[product.id] || qualifications[product.equinoxProductId] || qualifications[product.bundle.parentId]).sharableQuantity || 0 }}
            </div>
            <div class="after-block"></div>
          </div>
          {{ tdc.tdc_remainingInventory }}
        </div>
      </header>
      <table class="people">
        <tr v-for="shared in (sharedMap[product.equinoxProductId] || sharedMap[product.id] || sharedMap[product.bundle.parentId])" :key="shared.customerId">
          <td class="name">{{ usernameMappings[shared.customerId] }}</td>

          <td class="status" v-if="shared.consumedQuantity > 0">
            <ns-icon icon-name="icon-cart-check" class="progress-color" size="icon-md" /> {{ tdc.tdc_purchased }} {{ shared.consumedQuantity }}/{{ shared.quantity }}
          </td>
          <td class="status" v-else-if="shared.claimStatus === 'ALL'">
            <ns-icon icon-name="icon-products" class="all" /> {{ tdc.tdc_claimedAll }}
          </td>
          <td class="status" v-else-if="shared.claimStatus === 'SOME'">
            <ns-icon icon-name="icon-products-partial" class="some" /> {{ tdc.tdc_claimedSome }}
          </td>
          <td class="status" v-else-if="shared.claimStatus === 'NONE'">
            <ns-icon icon-name="icon-products" class="none" /> {{ tdc.tdc_claimedNone }}
          </td>
          <td class="status" v-else-if="shared.viewedQuantity > 0">
            <ns-icon icon-name="icon-email-opened" /> {{ tdc.tdc_viewed }}
          </td>
          <td class="status" v-else>
            <ns-icon icon-name="icon-email" /> {{ tdc.tdc_sent }}
          </td>
          <td class="quantity">{{ tdc.tdc_statusQuantity }} {{ shared.quantity }}</td>
          <td class="reclaim">
            <button
              :id="'reclaimLink_' + product.id + '_' + (qualifications[product.equinoxProductId] || qualifications[product.id] || qualifications[product.bundle.parentId]).customerId"
              class="link"
              :disabled="!shared.canReclaim"
              @click="openReclaim((qualifications[product.equinoxProductId] || qualifications[product.id] || qualifications[product.bundle.parentId]).customerId, (qualifications[product.equinoxProductId] || qualifications[product.id] || qualifications[product.bundle.parentId]).productId, shared)"
            >
              <ns-icon icon-name="icon-reclaim" /> {{ tdc.tdc_reclaim }}
            </button>
          </td>
        </tr>
      </table>
    </div>
    <ReclaimModal
      :customerId="sharerId"
      :shared="sharedToInfo"
      :sharedSku="sharedSku"
      :show="showReclaimModal"
      @close-reclaim-modal="closeReclaimModal"
    />
  </div>
</template>

<script>
import { NsIcon } from '@nuskin/ns-icon'
import { events } from '@nuskin/ns-util'
import ReclaimModal from './reclaim/ReclaimModal.vue'

import QualificationService from '@nuskin/exclusive-offer-sdk'

import config from '../js/qualificationsConfigService'
import { mapIdsToDisplayNames } from '../js/VGHelper'

export default {
  name: 'TransferStatus',

  components: {
    NsIcon,
    ReclaimModal,
  },

  data() {
    return {
      quantity: '',
      sharedToInfo: null,
      reclaimSku: null,
      sharerId: null,
      sharedSku: null,
      showReclaimModal: false,
      tdc: config.getTdc(),
      usernameMappings: {}
    }
  },
  props: {
    qualifications: {
      default: () => {},
      type: Object
    },
    products: {
      default: () => {},
      type: Object
    }
  },
  mounted() {
    this.populateUserNames()
    QualificationService.qualificationsViewed(Object.keys(this.qualifications), true)
  },

  methods: {
    openReclaim(sharer, sharedSku, shared) {
      this.sharerId = sharer
      this.sharedSku = sharedSku
      this.sharedToInfo = shared
      this.showReclaimModal = true
    },
    async populateUserNames() {
      // get usernames
      const usernameList =
          Object.values(this.qualifications)
            .filter(qualification => qualification.sharedList && qualification.sharedList.length > 0)
            .flatMap(qualification => qualification.sharedList)
            .map(share => share.customerId)

      // look up the mappings
      this.usernameMappings = await mapIdsToDisplayNames(usernameList)
    },
    async closeReclaimModal(qualification) {
      if (qualification) {
        this.qualifications[qualification.productId] = qualification
        await this.$emit('items-reclaimed')
        events.publish(events.shop.QUALIFICATION_STATUS_CHANGED)
      }
      this.showReclaimModal = false
    },
  },

  computed: {
    sharedMap() {
      return Object.values(this.qualifications).reduce((map, qual) => {
        map[qual.productId] = qual.sharedList.filter((shared) => shared.quantity > 0)
        return map
      }, {})
    },
  },
}
</script>

<style lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";

#qualificationsView {
  .transfer-status {
    overflow-y: auto;
    padding: 0 4px;

    h3 {
      @include global.subhead-l();
      margin-bottom: 10px;
    }

    .card {
      padding: 20px 15px;
      box-sizing: border-box;
      border: 1px solid map.get(colors.$baseColors, "gandalf");
      border-radius: 6px;
      box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
      margin-bottom: 20px;
      background-color: map.get(colors.$baseColors, "basewhite");

      header {
        box-shadow: none !important;
        display: flex;
        align-items: center;
        width: 100%;
        text-align: left;
        margin-bottom: 0;
        padding: 0 0 15px 0;
        flex-wrap: wrap;

        h5 {
          @include global.heading-5();
          color: map.get(colors.$baseColors, "blackhole");
          flex-grow: 1;
        }
      }

      .remaining {
        @include global.heading-3();
        display: flex;
        color: var(--blackhole);
        align-items: center;
        margin-bottom: 0;

        @media screen and (max-width: 480px) {
          padding-top: 10px;
        }
      }

      .num-available {
        position: relative;
        display: inline-block;
        margin-bottom: 0;
        margin-left: 10px;

        .before-block {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background-color: var(--magic);
          position: absolute;
          left: -10px;
          top: 0;
          margin-bottom: 0;
        }

        .middle-block {
          font-size: 20px;
          font-weight: 600;
          line-height: 25px;
          background-color: var(--magic);
          color: white;
          height: 30px;
          position: relative;
          z-index: 2;
          margin-right: 20px;
          display: flex;
          align-items: center;
          border-radius: 10px;
          margin-bottom: 0;
        }

        .after-block {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background-color: var(--magic);
          position: absolute;
          right: 10px;
          top: 0;
          margin-bottom: 0;
        }
      }

      .product-img {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 30px;
        width: 30px;
        border: 1px solid map.get(colors.$baseColors, "gandalf");
        padding: 2px;
        border-radius: 6px;
        margin: 0 10px 0 0;

        img {
          max-width: 100%;
          max-height: 100%;
        }
      }

      .people {
        width: 100%;
        margin: 0;

        tr {
          &:not(:last-child) {
            border-bottom: 1px solid map.get(colors.$baseColors, "gandalf");
          }
        }

        td {
          vertical-align: middle;
          border: none;
          @include global.heading-4();
          padding: 10px 0;
          width: 25%;
          color: map.get(colors.$baseColors, "blackhole");

          .icon {
            margin-right: 10px;
            display: inline !important;
            vertical-align: middle;
          }

          .link {
            text-align: right;
            color: map.get(colors.$baseColors, "shells");
            float: right;

            &:disabled {
              color: map.get(colors.$baseColors, "eyore");
            }
          }

          &.name {
            text-align: left;
            width: 30%;
          }

          &.status {
            width: 30%;
            text-align: left;
          }

          &.quantity {
            text-align: left;
            width: 20%;
          }

          &.reclaim {
            width: 20%;
            text-align: right;
          }

          @media screen and (max-width: 480px) {
            &.quantity,
            &.status {
            text-align: center;
            }
          }
        }

        @media screen and (max-width: 480px) {
          tr {
            display: flex;
            flex-wrap: wrap;

            td {
              width: 50% !important;

              &.name {
                order: 1;
              }

              &.reclaim {
                order: 2;
                text-align: right;
              }

              &.status {
                order: 3;
                text-align: left;
              }

              &.quantity {
                order: 4;
                text-align: right;
              }
            }
          }
        }
      }
    }
    .display-none {
      visibility: hidden;
    }
  }

  .progress-color {
    color: map.get(colors.$baseColors, "progress-color");
  }

  .all {
    color: map.get(colors.$baseColors, "progress-color");
  }
  .some {
    color: map.get(colors.$baseColors, "warning-color");
  }
  .none {
    color: map.get(colors.$baseColors, "color-error");
  }
}
</style>
